import React, { useState, useEffect } from 'react';
import { Info, Share2 } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './components/ui/Tooltip';
import { Input } from './components/ui/Input';
import { Label } from './components/ui/Label';
import { Select } from './components/ui/Select';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './components/ui/Accordion';

const ConfigOption = ({ label, name, type, defaultValue, info, options, onChange, value, showIf }) => {
  // If showIf is defined and returns false, don't render the option
  if (showIf && !showIf(window.configState)) {
    return null;
  }

  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(name, newValue);
    // Update the global config state for conditional rendering
    window.configState = {
      ...window.configState,
      [name]: newValue
    };
  };

  return (
    <div className="mb-4">
      <div className="flex items-center mb-2">
        <Label htmlFor={name} className="mr-2 text-[#fcb463]">{label}</Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" onClick={(e) => e.preventDefault()}>
                <Info size={16} className="text-white cursor-help" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{info}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      {type === 'select' ? (
        <Select
          id={name}
          name={name}
          value={value}
          onChange={handleChange}
          className="w-full bg-[#262624] text-[#fcce9b] border-[#fcb463]"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      ) : (
        <Input
          type={type}
          id={name}
          name={name}
          value={value}
          onChange={handleChange}
          className="w-full"
        />
      )}
    </div>
  );
};

const TopBar = () => (
  <div className="w-full bg-[#302f2e] py-4">
    <div className="max-w-2xl mx-auto px-6 text-center">
      <h1 className="text-2xl font-bold text-[#fcb463]">Klipper Firmware Configurator</h1>
    </div>
  </div>
);

const Footer = () => (
  <div className="w-full bg-[#302f2e] py-4">
    <div className="max-w-2xl mx-auto flex items-center justify-center px-6">
      <span className="text-[#fcb463] mr-2">Powered by</span>
      <a href="https://kb-3d.com"><img src="https://kb-3d.com/store/img/cms/KB3DLogoWEB.svg" alt="KB3D Logo" className="h-8" /></a>
    </div>
  </div>
);

const KlipperConfigInterface = () => {
  const [config, setConfig] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);

const configGroups = [
    {
      title: "Printer Kinematics",
      options: [
        {
          label: "Printer Kinematics",
          name: "kinematics",
          type: "select",
          defaultValue: "cartesian",
          info: "The type of printer kinematics",
          options: [
            { value: "cartesian", label: "Cartesian" },
            { value: "corexy", label: "CoreXY" },
            { value: "corexz", label: "CoreXZ" },
            { value: "hybrid_corexy", label: "Hybrid CoreXY" },
            { value: "hybrid_corexz", label: "Hybrid CoreXZ" },
            { value: "delta", label: "Delta" }
          ]
        }
      ]
    },
	{
	  title: "Printer Dimensions",
	  options: [
		{
		  label: "X Max Position",
		  name: "x_position_max",
		  type: "number",
		  defaultValue: "235",
		  info: "Maximum X position in millimeters (width of print area)"
		},
		{
		  label: "Y Max Position",
		  name: "y_position_max",
		  type: "number",
		  defaultValue: "235",
		  info: "Maximum Y position in millimeters (depth of print area)"
		},
		{
		  label: "Z Max Position",
		  name: "z_position_max",
		  type: "number",
		  defaultValue: "250",
		  info: "Maximum Z position in millimeters (height of print area)"
		}
	  ]
	},
    {
      title: "Motion Settings",
      options: [
        {
          label: "Max Acceleration",
          name: "max_accel",
          type: "number",
          defaultValue: "3000",
          info: "Maximum acceleration (mm/s^2) of the toolhead. Range: 500-10000"
        },
        {
          label: "Max Velocity",
          name: "max_velocity",
          type: "number",
          defaultValue: "300",
          info: "Maximum velocity (mm/s) of the toolhead. Range: 100-1000"
        },
        {
          label: "Square Corner Velocity",
          name: "square_corner_velocity",
          type: "number",
          defaultValue: "5",
          info: "Maximum velocity (mm/s) for cornering. Range: 1-10"
        },
        {
          label: "Max Z Velocity",
          name: "max_z_velocity",
          type: "number",
          defaultValue: "5",
          info: "Maximum Z axis velocity (mm/s)"
        },
        {
          label: "Max Z Acceleration",
          name: "max_z_accel",
          type: "number",
          defaultValue: "100",
          info: "Maximum Z axis acceleration (mm/s^2)"
        }
      ]
    },
    {
      title: "Extruder",
      options: [
        {
          label: "Nozzle Diameter",
          name: "nozzle_diameter",
          type: "number",
          defaultValue: "0.4",
          info: "The diameter of the nozzle orifice (in mm)"
        },
        {
          label: "Filament Diameter",
          name: "filament_diameter",
          type: "number",
          defaultValue: "1.75",
          info: "The nominal diameter of the raw filament (in mm)"
        },
        {
          label: "Max Extrude Only Distance",
          name: "max_extrude_only_distance",
          type: "number",
          defaultValue: "100",
          info: "Maximum length (in mm) that a retraction or extrude command may use"
        },
        {
          label: "Max Extrude Only Velocity",
          name: "max_extrude_only_velocity",
          type: "number",
          defaultValue: "50",
          info: "Maximum velocity (mm/s) of extrude only moves"
        },
        {
          label: "Max Extrude Only Accel",
          name: "max_extrude_only_accel",
          type: "number",
          defaultValue: "1000",
          info: "Maximum acceleration (mm/s^2) of extrude only moves"
        },
        {
          label: "Pressure Advance",
          name: "pressure_advance",
          type: "number",
          defaultValue: "0",
          info: "The amount of raw filament to push into the extruder during acceleration"
        }
      ]
    },
    {
      title: "Bed Leveling",
      options: [
        {
          label: "Bed Leveling",
          name: "bed_leveling",
          type: "select",
          defaultValue: "none",
          info: "The type of bed leveling to use",
          options: [
            { value: "none", label: "None" },
            { value: "3point", label: "3-Point" },
            { value: "bltouch", label: "BLTouch" },
            { value: "manual", label: "Manual Mesh" }
          ]
        },
        {
          label: "Mesh Min X",
          name: "mesh_min_x",
          type: "number",
          defaultValue: "35",
          info: "The minimum X coordinate of the mesh (in mm)"
        },
        {
          label: "Mesh Min Y",
          name: "mesh_min_y",
          type: "number",
          defaultValue: "35",
          info: "The minimum Y coordinate of the mesh (in mm)"
        },
        {
          label: "Mesh Max X",
          name: "mesh_max_x",
          type: "number",
          defaultValue: "215",
          info: "The maximum X coordinate of the mesh (in mm)"
        },
        {
          label: "Mesh Max Y",
          name: "mesh_max_y",
          type: "number",
          defaultValue: "215",
          info: "The maximum Y coordinate of the mesh (in mm)"
        },
        {
          label: "Probe X Offset",
          name: "probe_x_offset",
          type: "number",
          defaultValue: "0",
          info: "X offset of the probe relative to the nozzle"
        },
        {
          label: "Probe Y Offset",
          name: "probe_y_offset",
          type: "number",
          defaultValue: "0",
          info: "Y offset of the probe relative to the nozzle"
        }
      ]
    },
	{
	  title: "Stepper Motor (X)",
	  options: [
		{
		  label: "X Rotation Distance",
		  name: "x_rotation_distance",
		  type: "number",
		  defaultValue: "40",
		  info: "The rotation distance in mm. For most belt-driven printers with 20 tooth pulleys, this is 40mm"
		},
		{
		  label: "X Stepper Current",
		  name: "x_stepper_current",
		  type: "number",
		  defaultValue: "0.8",
		  info: "RMS current for X stepper in amps"
		},
		{
		  label: "X Endstop Position",
		  name: "x_endstop_position",
		  type: "select",
		  defaultValue: "min",
		  info: "Location of the X endstop",
		  options: [
			{ value: "min", label: "Minimum (Left)" },
			{ value: "max", label: "Maximum (Right)" }
		  ]
		},
		{
		  label: "X Homing Direction",
		  name: "x_homing_direction",
		  type: "select",
		  defaultValue: "positive",
		  info: "Direction to home X axis. For CoreXY/CoreXZ, this must match your belt configuration",
		  options: [
			{ value: "positive", label: "Positive (Right)" },
			{ value: "negative", label: "Negative (Left)" }
		  ]
		}
	  ]
	},
	{
	  title: "Stepper Motor (Y)",
	  options: [
		{
		  label: "Y Rotation Distance",
		  name: "y_rotation_distance",
		  type: "number",
		  defaultValue: "40",
		  info: "The rotation distance in mm. For most belt-driven printers with 20 tooth pulleys, this is 40mm"
		},
		{
		  label: "Y Stepper Current",
		  name: "y_stepper_current",
		  type: "number",
		  defaultValue: "0.8",
		  info: "RMS current for Y stepper in amps"
		},
		{
		  label: "Y Endstop Position",
		  name: "y_endstop_position",
		  type: "select",
		  defaultValue: "min",
		  info: "Location of the Y endstop",
		  options: [
			{ value: "min", label: "Minimum (Front)" },
			{ value: "max", label: "Maximum (Back)" }
		  ]
		},
		{
		  label: "Y Homing Direction",
		  name: "y_homing_direction",
		  type: "select",
		  defaultValue: "positive",
		  info: "Direction to home Y axis. For CoreXY, this must match your belt configuration",
		  options: [
			{ value: "positive", label: "Positive (Back)" },
			{ value: "negative", label: "Negative (Front)" }
		  ]
		}
	  ]
	},
	{
	  title: "Stepper Motor (Z)",
	  options: [
		{
		  label: "Z Rotation Distance",
		  name: "z_rotation_distance",
		  type: "number",
		  defaultValue: "8",
		  info: "The rotation distance in mm. For most leadscrews, this is 8mm (for T8 screws) or 4mm (for T8 integrated leadscrew)"
		},
		{
		  label: "Z Stepper Current",
		  name: "z_stepper_current",
		  type: "number",
		  defaultValue: "0.8",
		  info: "RMS current for Z stepper in amps"
		},
		{
		  label: "Z Endstop Position",
		  name: "z_endstop_position",
		  type: "select",
		  defaultValue: "min",
		  info: "Location of the Z endstop",
		  options: [
			{ value: "min", label: "Minimum (Bottom)" },
			{ value: "max", label: "Maximum (Top)" }
		  ]
		},
		{
		  label: "Z Homing Direction",
		  name: "z_homing_direction",
		  type: "select",
		  defaultValue: "positive",
		  info: "Direction to home Z axis. For CoreXZ, this must match your belt configuration",
		  options: [
			{ value: "positive", label: "Positive (Up)" },
			{ value: "negative", label: "Negative (Down)" }
		  ]
		}
	  ]
	},
	{
	  title: "Stepper Motor (E)",
	  options: [
		{
		  label: "E Rotation Distance",
		  name: "e_rotation_distance",
		  type: "number",
		  defaultValue: "34.4",
		  info: "The rotation distance in mm. For stock extruders this is typically around 34.4mm"
		},
		{
		  label: "E Stepper Current",
		  name: "e_stepper_current",
		  type: "number",
		  defaultValue: "0.7",
		  info: "RMS current for E stepper in amps"
		}
	  ]
	},
	{
	  title: "Stepper Features",
	  options: [
		{
		  label: "Sensorless Homing",
		  name: "sensorless_homing",
		  type: "select",
		  defaultValue: "none",
		  info: "Enable sensorless homing using TMC stallguard",
		  options: [
			{ value: "none", label: "Disabled" },
			{ value: "x", label: "X Only" },
			{ value: "y", label: "Y Only" },
			{ value: "xy", label: "X and Y" }
		  ]
		}
	  ]
	},
    {
      title: "Temperature Control",
      options: [
        {
          label: "Extruder Max Temp",
          name: "extruder_max_temp",
          type: "number",
          defaultValue: "250",
          info: "Maximum temperature (in °C) that the extruder can reach"
        },
        {
          label: "Bed Max Temp",
          name: "bed_max_temp",
          type: "number",
          defaultValue: "100",
          info: "Maximum temperature (in °C) that the bed can reach"
        },
        {
          label: "Temperature Control",
          name: "temp_control",
          type: "select",
          defaultValue: "pid_recommended",
          info: "Temperature control method. PID values can be auto-tuned using the 'PID_CALIBRATE HEATER=extruder TARGET=200' command in Klipper",
          options: [
            { value: "pid_recommended", label: "PID (Recommended)" },
            { value: "pid_custom", label: "PID (Custom)" },
            { value: "watermark", label: "Watermark" }
          ]
        },
        {
          label: "Extruder PID P",
          name: "extruder_pid_p",
          type: "number",
          defaultValue: "22.2",
          info: "Proportional value for extruder PID. Can be auto-tuned in Klipper.",
          showIf: (config) => config.temp_control === "pid_custom"
        },
        {
          label: "Extruder PID I",
          name: "extruder_pid_i",
          type: "number",
          defaultValue: "1.08",
          info: "Integral value for extruder PID. Can be auto-tuned in Klipper.",
          showIf: (config) => config.temp_control === "pid_custom"
        },
        {
          label: "Extruder PID D",
          name: "extruder_pid_d",
          type: "number",
          defaultValue: "114",
          info: "Derivative value for extruder PID. Can be auto-tuned in Klipper.",
          showIf: (config) => config.temp_control === "pid_custom"
        }
      ]
    },
    {
      title: "Retraction",
      options: [
        {
          label: "Retract Length",
          name: "retract_length",
          type: "number",
          defaultValue: "0.8",
          info: "The length of filament (in mm) to retract"
        },
        {
          label: "Retract Speed",
          name: "retract_speed",
          type: "number",
          defaultValue: "30",
          info: "The speed (in mm/s) at which to retract"
        },
        {
          label: "Unretract Extra Length",
          name: "unretract_extra_length",
          type: "number",
          defaultValue: "0",
          info: "Extra length of filament (in mm) to add when unretracting"
        },
        {
          label: "Unretract Speed",
          name: "unretract_speed",
          type: "number",
          defaultValue: "30",
          info: "The speed (in mm/s) at which to unretract"
        }
      ]
    }
];

  const allOptions = configGroups.flatMap(group => group.options);

  const parseValue = (value, type) => {
    if (type === 'number') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return value;
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const loadedConfig = {};
    allOptions.forEach(option => {
      if (params.has(option.name)) {
        loadedConfig[option.name] = parseValue(params.get(option.name), option.type);
      } else {
        loadedConfig[option.name] = parseValue(option.defaultValue, option.type);
      }
    });
	window.configState = loadedConfig;
    setConfig(loadedConfig);
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (isLoaded) {
      const params = new URLSearchParams();
      Object.entries(config).forEach(([key, value]) => {
        params.set(key, value.toString());
      });
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    }
  }, [config, isLoaded]);

  const handleConfigChange = (name, value) => {
    const option = allOptions.find(o => o.name === name);
    setConfig(prevConfig => ({
      ...prevConfig,
      [name]: parseValue(value, option.type)
    }));
  };

	const generateConfig = (e) => {
		e.preventDefault();
		let configText = "# Klipper Configuration File\n";
		configText += "# Generated by KB-3D.com Configurator\n";
		configText += "# Note: You MUST review and adjust all pin assignments for your specific board\n\n";

		// Printer section
		configText += "[printer]\n";
		if (config.kinematics !== 'cartesian') {
		  configText += `kinematics: ${config.kinematics}\n`;
		}
		configText += `max_velocity: ${config.max_velocity}\n`;
		configText += `max_accel: ${config.max_accel}\n`;
		configText += `max_accel_to_decel: ${config.max_accel / 2}\n`;
		configText += `max_z_velocity: ${config.max_z_velocity}\n`;
		configText += `max_z_accel: ${config.max_z_accel}\n`;
		configText += `square_corner_velocity: ${config.square_corner_velocity}\n\n`;

		// Stepper X
		configText += "[stepper_x]\n";
		configText += "# REVIEW REQUIRED: Adjust these pin configurations for your board\n";
		configText += "#step_pin: PC2\n";
		configText += `#dir_pin: ${config.x_homing_direction === "positive" ? "" : "!"}PB9\n`;
		configText += "#enable_pin: !PC3\n";
		if (config.x_endstop_position === "min") {
			configText += "#endstop_pin: ^PA5\n";
			configText += "position_endstop: 0\n";
		} else {
			configText += "#endstop_pin: ^PA6\n";
			configText += `position_endstop: ${config.x_position_max}\n`;
		}
		configText += `rotation_distance: ${config.x_rotation_distance}\n`;
		configText += "microsteps: 16\n";
		configText += "position_min: 0\n";
		configText += `position_max: ${config.x_position_max}\n`;
		configText += `run_current: ${config.x_stepper_current}\n`;
		if (config.sensorless_homing === "x" || config.sensorless_homing === "xy") {
			configText += "diag_pin: ^PA1      # Set to MCU pin connected to TMC DIAG pin\n";
			configText += "driver_SGTHRS: 100  # 255 is most sensitive value, 0 is least sensitive\n";
		}
		configText += "\n";

		// Stepper Y
		configText += "[stepper_y]\n";
		configText += "# REVIEW REQUIRED: Adjust these pin configurations for your board\n";
		configText += "#step_pin: PC2\n";
		configText += `#dir_pin: ${config.y_homing_direction === "positive" ? "" : "!"}PB9\n`;
		configText += "#enable_pin: !PC3\n";
		if (config.y_endstop_position === "min") {
			configText += "#endstop_pin: ^PA5\n";
			configText += "position_endstop: 0\n";
		} else {
			configText += "#endstop_pin: ^PA6\n";
			configText += `position_endstop: ${config.y_position_max}\n`;
		}
		configText += `rotation_distance: ${config.y_rotation_distance}\n`;
		configText += "microsteps: 16\n";
		configText += "position_min: 0\n";
		configText += `position_max: ${config.y_position_max}\n`;
		configText += `run_current: ${config.y_stepper_current}\n`;
		if (config.sensorless_homing === "y" || config.sensorless_homing === "xy") {
			configText += "diag_pin: ^PA2      # Set to MCU pin connected to TMC DIAG pin\n";
			configText += "driver_SGTHRS: 100  # 255 is most sensitive value, 0 is least sensitive\n";
		}
		configText += "\n";

		// Stepper Z
		configText += "[stepper_z]\n";
		configText += "# REVIEW REQUIRED: Adjust these pin configurations for your board\n";
		configText += "#step_pin: PC2\n";
		configText += `#dir_pin: ${config.z_homing_direction === "positive" ? "" : "!"}PB9\n`;
		configText += "#enable_pin: !PC3\n";
		if (config.z_endstop_position === "min") {
			configText += "#endstop_pin: ^PA7\n";
			configText += "position_endstop: 0\n";
		} else {
			configText += "#endstop_pin: ^PA8\n";
			configText += `position_endstop: ${config.z_position_max}\n`;
		}
		configText += `rotation_distance: ${config.z_rotation_distance}\n`;
		configText += "microsteps: 16\n";
		configText += "position_min: 0\n";
		configText += `position_max: ${config.z_position_max}\n`;
		configText += `run_current: ${config.z_stepper_current}\n\n`;

		// Extruder
		configText += "[extruder]\n";
		configText += "# REVIEW REQUIRED: Adjust these pin configurations for your board\n";
		configText += "#step_pin: PC2\n";
		configText += "#dir_pin: !PB9\n";
		configText += "#enable_pin: !PC3\n";
		configText += "#heater_pin: PC8\n";
		configText += "#sensor_pin: PA0\n";
		configText += `rotation_distance: ${config.e_rotation_distance}\n`;
		configText += "microsteps: 16\n";
		configText += `run_current: ${config.e_stepper_current}\n`;
		configText += `nozzle_diameter: ${config.nozzle_diameter}\n`;
		configText += `filament_diameter: ${config.filament_diameter}\n`;
		configText += `max_extrude_only_distance: ${config.max_extrude_only_distance}\n`;
		configText += `max_extrude_only_velocity: ${config.max_extrude_only_velocity}\n`;
		configText += `max_extrude_only_accel: ${config.max_extrude_only_accel}\n`;
		configText += `pressure_advance: ${config.pressure_advance}\n`;
		configText += "sensor_type: EPCOS 100K B57560G104F\n";
		configText += "min_temp: 0\n";
		configText += `max_temp: ${config.extruder_max_temp}\n`;
		if (config.temp_control === 'pid_recommended' || config.temp_control === 'pid_custom') {
			configText += "control: pid\n";
			if (config.temp_control === 'pid_custom') {
				configText += `pid_Kp: ${config.extruder_pid_p}\n`;
				configText += `pid_Ki: ${config.extruder_pid_i}\n`;
			  configText += `pid_Kd: ${config.extruder_pid_d}\n`;
			} else {
				// Add comment about PID tuning for recommended option
				configText += "# Use PID_CALIBRATE HEATER=extruder TARGET=200 command to auto-tune PID values\n";
				configText += "# Then use SAVE_CONFIG to save the tuned values\n";
			}
		}
		configText += "\n";

		// Heated Bed
		configText += "[heater_bed]\n";
		configText += "# REVIEW REQUIRED: Adjust these pin configurations for your board\n";
		configText += "#heater_pin: PC9\n";
		configText += "#sensor_pin: PC3\n";
		configText += "sensor_type: EPCOS 100K B57560G104F\n";
		configText += "min_temp: 0\n";
		configText += `max_temp: ${config.bed_max_temp}\n`;
		if (config.temp_control === 'pid') {
			configText += "control: pid\n";
			configText += "pid_Kp: 54.027\n";
			configText += "pid_Ki: 0.770\n";
			configText += "pid_Kd: 948.182\n";
		}
		configText += "\n";

		// Bed Leveling
		if (config.bed_leveling !== 'none') {
			if (config.bed_leveling === 'bltouch') {
				configText += "[bltouch]\n";
				configText += "# REVIEW REQUIRED: Adjust these pin configurations for your board\n";
				configText += "#sensor_pin: ^PC14\n";
				configText += "#control_pin: PC13\n";
				configText += `x_offset: ${config.probe_x_offset}\n`;
				configText += `y_offset: ${config.probe_y_offset}\n`;
				configText += "z_offset: 2.345\n\n";
			}
		  
			configText += "[bed_mesh]\n";
			configText += "speed: 120\n";
			configText += "horizontal_move_z: 5\n";
			configText += `mesh_min: ${config.mesh_min_x}, ${config.mesh_min_y}\n`;
			configText += `mesh_max: ${config.mesh_max_x}, ${config.mesh_max_y}\n`;
			configText += "probe_count: 5,5\n";
			configText += "algorithm: bicubic\n";
			configText += "fade_start: 1\n";
			configText += "fade_end: 10\n\n";
		}

		// Firmware retraction if enabled
		if (parseFloat(config.retract_length) > 0) {
			configText += "[firmware_retraction]\n";
			configText += `retract_length: ${config.retract_length}\n`;
			configText += `retract_speed: ${config.retract_speed}\n`;
			configText += `unretract_extra_length: ${config.unretract_extra_length}\n`;
			configText += `unretract_speed: ${config.unretract_speed}\n\n`;
		}

		// Required additional sections
		configText += "# Required additional features\n";
		configText += "[virtual_sdcard]\n";
		configText += "path: ~/gcode_files\n\n";
		
		configText += "[display_status]\n\n";
		
		configText += "[pause_resume]\n\n";
		
		configText += "[gcode_macro PAUSE]\n";
		configText += "description: Pause the current print\n";
		configText += "rename_existing: BASE_PAUSE\n";
		configText += "gcode:\n";
		configText += "  SAVE_GCODE_STATE NAME=PAUSE_state\n";
		configText += "  BASE_PAUSE\n";
		configText += "  {% set z = params.Z|default(10)|float %}\n";
		configText += "  {% set e = params.E|default(1)|float %}\n";
		configText += "  G91\n";
		configText += "  G1 E-{e} F2100\n";
		configText += "  G1 Z{z}\n";
		configText += "  G90\n";
		configText += "  G1 X0 Y0 F6000\n\n";

		configText += "[gcode_macro RESUME]\n";
		configText += "description: Resume the current print\n";
		configText += "rename_existing: BASE_RESUME\n";
		configText += "gcode:\n";
		configText += "  {% set e = params.E|default(1)|float %}\n";
		configText += "  G91\n";
		configText += "  G1 E{e} F2100\n";
		configText += "  G90\n";
		configText += "  RESTORE_GCODE_STATE NAME=PAUSE_state MOVE=1\n";
		configText += "  BASE_RESUME\n\n";

		configText += "[gcode_macro CANCEL_PRINT]\n";
		configText += "description: Cancel the current print\n";
		configText += "rename_existing: BASE_CANCEL_PRINT\n";
		configText += "gcode:\n";
		configText += "  TURN_OFF_HEATERS\n";
		configText += "  CLEAR_PAUSE\n";
		configText += "  SDCARD_RESET_FILE\n";
		configText += "  BASE_CANCEL_PRINT\n\n";

		const blob = new Blob([configText], { type: "text/plain" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = "printer.cfg";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

  const copyShareLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      alert("Share link copied to clipboard!");
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-[#262624] flex flex-col">
      <TopBar />
      <div className="flex-grow py-8">
        <div className="max-w-2xl mx-auto p-6 bg-[#302f2e] rounded shadow border border-[#fcb463]">
          <form onSubmit={generateConfig}>
            <Accordion type="single" collapsible className="w-full">
              {configGroups.map((group, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-[#fcb463]">{group.title}</AccordionTrigger>
                  <AccordionContent>
                    {group.options.map((option) => (
                      <ConfigOption 
                        key={option.name} 
                        {...option} 
                        onChange={handleConfigChange}
                        value={config[option.name]}
                      />
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="flex justify-between items-center mt-4">
              <button type="submit" className="bg-[#fcb463] text-[#262624] px-4 py-2 rounded hover:bg-[#fca443]">
                Generate Config
              </button>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button 
                      type="button" 
                      onClick={copyShareLink} 
                      className="text-[#fcb463] p-2 rounded-full hover:bg-[#3a3938] transition-colors"
                    >
                      <Share2 size={24} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Copy Share Link</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default KlipperConfigInterface;